import { useEffect, useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import {
  offerVlansToOnu,
  type CliActionResult,
  type OltVlanListResponse,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, ErrorNotice, Field, Input } from '../../components/ui';

/**
 * The subscriber WAN of an OMCI-managed ONU — DHCP or PPPoE terminated on
 * the ONU with no ACS involved, or removed. ZTE only, launched from the WAN
 * card's Edit. The VLAN comes from what this chassis actually carries, the
 * preview shows the exact commands with the PPPoE password already masked,
 * and applying warns that the connection renegotiates.
 *
 * WHICH writer the route takes is the server's decision, read off the dry
 * run (`writer` + `writerReason`), never assumed here: this dialog also
 * opens on a LINKED unit whose ACS document holds no subscriber WAN yet (the
 * ONU page's "WAN over OMCI · from the chassis" fallback), and since the
 * 2026-09-14 duplicate-WAN incident such a unit takes its WAN over TR-069
 * (`configureWan` creates the connection — the TR-069 default) with no
 * chassis line at all. The preview, the Review pill and the done text say
 * which; "over OMCI" is said only when the server chose the chassis.
 *
 * The ONU's own recorded VLANs are read here for one reason: `POST
 * /olts/:id/onus/:onuId/omci-wan` judges the VLAN against the same rule, and
 * treats one this ONU is already on as an edit rather than a new placement.
 * Offering a set the write side would refuse — or hiding one it would take —
 * is the failure this list exists to avoid, so both read the same fact.
 */
export function OmciWanDialog({
  oltId,
  onuId,
  currentMode,
  wanCvlan,
  wanIndex,
  onClose,
  href,
  invalidate,
}: {
  oltId: string;
  onuId: string;
  currentMode: string | null;
  /** The VLAN the ONU's WAN is on now — seeded so the select matches the card. */
  wanCvlan?: number | null;
  /** The WAN interface number to write back to, not a hardcoded 1. */
  wanIndex?: number | null;
  onClose: () => void;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the ONU page's own). */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  const [mode, setMode] = useState<'dhcp' | 'pppoe' | 'none'>('dhcp');
  const [vlanId, setVlanId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const vlans = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<OltVlanListResponse>(`/olts/${encodeURIComponent(oltId)}/vlans`),
  });
  const onuVlans = useQuery({
    queryKey: ['olt', oltId, 'onu', onuId, 'vlans'],
    queryFn: () =>
      api.get<{ items: Array<{ vlanId: number }> }>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/vlans`,
      ),
  });
  const offer = offerVlansToOnu(vlans.data?.items ?? [], {
    vlan1Enabled: vlans.data?.vlan1Selectable === true,
    alreadyOn: (onuVlans.data?.items ?? []).map((v) => v.vlanId),
  });
  const subscriberVlans = offer.offered;

  useEffect(() => {
    if (vlanId) return;
    // Seed the VLAN the ONU is ACTUALLY on, not the lowest the chassis carries
    // (VLAN 1 on this fleet). Opening the dialog on a subscriber riding VLAN 5
    // and pressing Write without touching the select used to move them to VLAN
    // 1, with nothing on screen saying the select disagreed with the card (§8.4).
    if (wanCvlan != null && subscriberVlans.some((v) => v.vlanId === wanCvlan)) {
      setVlanId(String(wanCvlan));
    } else if (subscriberVlans.length > 0) {
      setVlanId(String(subscriberVlans[0]!.vlanId));
    }
  }, [subscriberVlans, vlanId, wanCvlan]);

  /**
   * Undefined means LEAVE IT ALONE, and that is the default on purpose.
   * Opening this dialog to change a VLAN should not quietly re-decide whether
   * the subscriber's router answers the internet.
   */
  const [firewalled, setFirewalled] = useState<boolean | undefined>(undefined);

  const path = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-wan`;
  const body = (dryRun: boolean) => ({
    dryRun,
    mode,
    // Target the interface the WAN is actually on. Without it the write side
    // fell back to `wan-ip 1`, so every edit hit interface 1 regardless (§8.4).
    ...(wanIndex != null ? { index: wanIndex } : {}),
    ...(mode !== 'none' ? { vlanId: Number(vlanId) } : {}),
    ...(mode === 'pppoe' ? { username: username.trim(), password } : {}),
    ...(firewalled === undefined ? {} : { firewalled }),
  });
  const ready =
    mode === 'none' || (vlanId && (mode !== 'pppoe' || (username.trim() && password.length >= 1)));


  /**
   * Write hands the chassis change to the background tray and closes — like
   * Resync. The dry-run preview above stays in the dialog; only the write
   * itself moves off-screen so the operator is not pinned to a spinner while
   * the connection renegotiates.
   */
  const startApply = () => {
    const chosen = mode;
    const payload = body(false);
    onClose();
    tasks.start<CliActionResult>({
      label: `Subscriber WAN (OMCI) — ${onuId}`,
      ...(href ? { href } : {}),
      run: () => api.post<CliActionResult>(path, payload),
      // Plain (operator, 2026-09-19): what changed, and whether it is still
      // waiting for the ONU. Which writer took it is the ONU page's business.
      done: (r) => {
        const head = chosen === 'none' ? 'WAN removed' : `WAN set to ${chosen === 'pppoe' ? 'PPPoE' : 'DHCP'}`;
        const queued = r.executed === false;
        return `${head} — ${queued ? 'queued; it lands at the ONU’s next check-in' : 'applied'}.`;
      },
      invalidate: [['onu', onuId, 'omci-config'], ...(invalidate ?? [])],
    });
  };

  return (
    <WizardDialog
      title="Subscriber WAN through the OLT"
      // Until the dry run has said which writer takes it, the subtitle names
      // both: the chassis line where the OMCI terminates the WAN, the CPE's
      // own connection where the CPE does — the server reads the CPE and decides.
      subtitle={`The subscriber connection on this VLAN.${currentMode ? ` Currently ${currentMode}.` : ''}`}
      maxWidth="lg"
      // One step, straight to Apply (operator, 2026-09-19: no previews of
      // chassis commands anywhere; the tray says what landed).
      steps={[{ key: 'configure', title: 'Configure' }]}
      currentStep={0}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={startApply}>
            Apply
          </Button>
        </div>
      }
    >
      {error && <ErrorNotice error={error} />}

      <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Mode">
              <select
                className="input"
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value as never);
                 
                }}
              >
                <option value="dhcp">DHCP</option>
                <option value="pppoe">PPPoE</option>
                <option value="none">Remove the WAN</option>
              </select>
            </Field>
            {mode !== 'none' && (
              <Field
                label="Firewall"
                hint="Whether the ONU answers unsolicited traffic from the WAN side"
              >
                <select
                  className="input"
                  value={firewalled === undefined ? '' : firewalled ? 'on' : 'off'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFirewalled(v === '' ? undefined : v === 'on');
                   
                  }}
                >
                  <option value="">Leave as it is</option>
                  <option value="on">Firewalled — ignores unsolicited traffic</option>
                  <option value="off">Reachable — answers from the WAN side</option>
                </select>
              </Field>
            )}
            {mode !== 'none' && (
              <Field
                label="VLAN"
                hint={
                  offer.vlan1Withheld
                    ? 'What this OLT carries for subscribers. VLAN 1 is carried here but is not offered to subscribers for this organization.'
                    : 'What this OLT carries for subscribers'
                }
              >
                <select
                  className="input"
                  value={vlanId}
                  onChange={(e) => {
                    setVlanId(e.target.value);
                   
                  }}
                >
                  {subscriberVlans.map((v) => (
                    <option key={v.id} value={v.vlanId}>
                      {v.vlanId}
                      {v.description ? ` — ${v.description}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {mode === 'pppoe' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="PPPoE username">
                <Input value={username} maxLength={128} onChange={(e) => { setUsername(e.target.value); }} />
              </Field>
              <Field label="PPPoE password" hint="Stored encrypted, never shown again">
                <Input type="password" value={password} maxLength={128} onChange={(e) => { setPassword(e.target.value); }} />
              </Field>
            </div>
          )}

    </WizardDialog>
  );
}
